import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Spinner } from 'react-bootstrap';

/**
 * Generation Progress Component
 * Shows step-by-step progress during article processing with real-time updates
 * Only renders the white card content - header is handled by Main.js
 */
const GenerationProgress = ({ progress = {}, audioMode = 'summary' }) => {
  const [showExtendedMessage, setShowExtendedMessage] = useState(false);

  // Show reassurance message after 60 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowExtendedMessage(true);
    }, 60000);

    return () => clearTimeout(timer);
  }, []);

  const generationSteps = [
    { id: 'extract', name: 'Extracting article content' },
    { id: 'audio', name: audioMode === 'summary' ? 'Spawning audio summary' : 'Spawning audio narration' },
    { id: 'social', name: 'Spawning social posts' },
    { id: 'images', name: 'Spawning images' },
    { id: 'finalizing', name: 'Finalizing your media' }
  ];

  const getStepStatus = (stepId) => {
    const stepProgress = progress[stepId];
    if (!stepProgress) return 'pending';
    return stepProgress.status;
  };

  const getStepDetail = (stepId) => {
    const stepProgress = progress[stepId];
    if (stepId === 'images' && stepProgress?.total) {
      return `(${stepProgress.completed || 0}/${stepProgress.total})`;
    }
    return null;
  };

  const renderStepIcon = (status, index) => {
    if (status === 'completed') {
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
          <polyline points="20 6 9 17 4 12"></polyline>
        </svg>
      );
    }
    if (status === 'in_progress') {
      return <Spinner animation="border" size="sm" style={{ width: '12px', height: '12px' }} />;
    }
    return index + 1;
  };

  const getStepStyle = (status) => {
    if (status === 'completed') {
      return { background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: '#fff' };
    }
    if (status === 'in_progress') {
      return { background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', color: '#fff' };
    }
    return { backgroundColor: '#e2e8f0', color: '#94a3b8' };
  };

  return (
    <Row className="justify-content-center">
      <Col lg={6} md={8}>
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '2rem',
          boxShadow: '0 4px 24px rgba(0,0,0,0.08)'
        }}>
          {generationSteps.map((step, index) => {
            const status = getStepStatus(step.id);
            const detail = getStepDetail(step.id);
            return (
              <div
                key={step.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '1rem 0',
                  borderBottom: index < generationSteps.length - 1 ? '1px solid #f1f5f9' : 'none'
                }}
              >
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '1rem',
                    fontSize: '13px',
                    fontWeight: '600',
                    flexShrink: 0,
                    ...getStepStyle(status)
                  }}
                >
                  {renderStepIcon(status, index)}
                </div>
                <span style={{
                  color: status === 'pending' ? '#94a3b8' : '#1a1a2e',
                  fontWeight: status === 'in_progress' ? '600' : '500',
                  fontSize: '0.95rem'
                }}>
                  {step.name}
                  {detail && <span style={{ color: '#94a3b8', marginLeft: '0.5rem', fontSize: '0.875rem' }}>{detail}</span>}
                </span>
              </div>
            );
          })}

          <p style={{ color: '#94a3b8', marginTop: '1.5rem', marginBottom: 0, fontSize: '0.875rem', textAlign: 'center' }}>
            {showExtendedMessage
              ? "Still working on it — larger articles may take a bit longer. Please don't close this page!"
              : "This may take 30-60 seconds. Please don't close this page."}
          </p>
        </div>
      </Col>
    </Row>
  );
};

export default GenerationProgress;
